import type { RefObject } from "react";
import React from "react";
import { useRef, useState } from "react";
import "./gradient-badge.scss";

export type TooltipPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export type GradientBadgeTooltipProps = {
  placement: TooltipPlacement;
  showTooltip: boolean;
  parentRef: RefObject<HTMLDivElement>;
  maxWidth: string;
  children: React.ReactNode;
};

export interface GradientBadgeProps {
  allIssueLabels: string[];
  label: string;
  gradient: React.CSSProperties;
  variant?: "red" | "green";
  iconRed?: React.ReactNode;
  iconGreen?: React.ReactNode;
  TooltipComponent: React.ComponentType<GradientBadgeTooltipProps>;
  tooltipBulletIcon?: React.ReactNode;
  style?: React.CSSProperties;
  tooltipPlacement?: TooltipPlacement;
  tooltipMaxWidth?: string;
  onClick?: () => void;
}

export const GradientBadge = ({
  allIssueLabels,
  label,
  gradient,
  variant = "red",
  iconRed,
  iconGreen,
  TooltipComponent,
  tooltipBulletIcon,
  style: styleProp,
  tooltipPlacement = "bottom",
  tooltipMaxWidth = "20rem",
  onClick,
}: GradientBadgeProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);
  const showTooltipContent =
    variant === "red" && showTooltip && allIssueLabels.length > 0;

  const leftIcon = variant === "green" ? iconGreen : iconRed;

  const containerStyle = {
    ...gradient,
    ...styleProp,
  };

  return (
    <div
      ref={badgeRef}
      style={containerStyle}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className="gradient-badge"
    >
      {leftIcon}
      <span className="gradient-badge__label">{label}</span>
      {showTooltipContent && (
        <TooltipComponent
          placement={tooltipPlacement}
          showTooltip={showTooltip}
          parentRef={badgeRef}
          maxWidth={tooltipMaxWidth}
        >
          <ul className="gradient-badge-tooltip-list">
            {allIssueLabels.map((line, i) => (
              <li key={i} className="gradient-badge-tooltip-item">
                {tooltipBulletIcon}
                {line}
              </li>
            ))}
          </ul>
        </TooltipComponent>
      )}
    </div>
  );
};

export default GradientBadge;
